import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { cameFromSessions, sessionsReturnPath } from '@/features/sessions/returnTo';
import { useQuery } from 'react-query';
import { Play, Pause, Maximize2, Minimize2, ArrowLeft, Clock, Rewind, Video, User, Monitor, SquareTerminal, Palette, TriangleAlert, RotateCw } from 'lucide-react';
import { format } from 'date-fns';
import type { Terminal } from '@xterm/xterm';
import TerminalSurface from '@/components/terminal/TerminalSurface';
import { sessionsApi } from '../services/api';
import { Breadcrumbs, Button, Chip, EmptyState, IconButton, LoadingRegion, PageHeader, Slider, Spinner } from '@/components/ds-zta';
import { replayTerminalTheme, canvasBackground, guacColor } from '@/lib/terminalTheme';
import { errorMessage } from '@/lib/http';
import { isNotFoundError } from '@/lib/apiError';

interface RecordingEvent { timestamp: string; type: string; data?: any; offset_ms: number; }
interface RecordingData { session_id: string; session_type?: string; events: RecordingEvent[]; event_count: number; start_time: string; end_time: string; duration_ms: number; }

// ---- Guacamole canvas rendering (shared with DesktopSession.tsx) ----

const COMPOSITE_OPS: Record<number, GlobalCompositeOperation> = {
    0x1: 'destination-in', 0x2: 'destination-out', 0x4: 'source-in',
    0x6: 'source-atop', 0x8: 'source-out', 0x9: 'destination-atop',
    0xA: 'xor', 0xB: 'destination-over', 0xC: 'copy',
    0xE: 'source-over', 0xF: 'lighter',
};
function getCompositeOp(mask: number): GlobalCompositeOperation {
    return COMPOSITE_OPS[mask] || 'source-over';
}

interface LayerState { canvas: HTMLCanvasElement; x: number; y: number; parent: number; }

function parseGuacInstructions(data: string): { opcode: string; args: string[] }[] {
    const results: { opcode: string; args: string[] }[] = [];
    let pos = 0;
    while (pos < data.length) {
        const elements: string[] = [];
        let terminated = false;
        while (pos < data.length) {
            const dotPos = data.indexOf('.', pos);
            if (dotPos === -1) break;
            const len = parseInt(data.substring(pos, dotPos));
            if (isNaN(len)) break;
            const start = dotPos + 1;
            elements.push(data.substring(start, start + len));
            pos = start + len;
            if (pos < data.length) {
                const sep = data[pos]; pos++;
                if (sep === ';') { terminated = true; break; }
                if (sep !== ',') break;
            }
        }
        if (terminated && elements.length > 0) results.push({ opcode: elements[0], args: elements.slice(1) });
        else break;
    }
    return results;
}

// ---- End Guacamole rendering ----

export default function SessionPlayerPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    // Back returns to the Sessions view the user came from (its filters and
    // page are in its URL): history's Back when they came from it, else the
    // last Sessions query this tab saw (design eval F24).
    const location = useLocation();
    const fromSessions = cameFromSessions(location.state);
    const backToSessions = () => (fromSessions ? navigate(-1) : navigate(sessionsReturnPath()));

    // Terminal replay: the screen is a TerminalSurface (design eval F40).
    const terminalRef = useRef<Terminal | null>(null);

    // Common state
    const [isPlaying, setIsPlaying] = useState(false);
    const [speed, setSpeed] = useState(1);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const playTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [outputEvents, setOutputEvents] = useState<{ data: string; offset_ms: number }[]>([]);
    const [sessionMeta, setSessionMeta] = useState<any>(null);

    // Desktop: the browser recording, a WebM the session's own browser made
    // with MediaRecorder and uploaded (secondary, design eval F20 / D2 = A).
    const videoRef = useRef<HTMLVideoElement>(null);
    const [videoBlobUrl, setVideoBlobUrl] = useState<string | null>(null);
    const [videoReady, setVideoReady] = useState(false);

    // Desktop: canvas replay of the server's recorded events (the evidence).
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const layersRef = useRef<Map<number, LayerState>>(new Map());
    const streamsRef = useRef<Map<string, { type: string; layer?: number; mode?: number; mimetype: string; x?: number; y?: number; data: string }>>(new Map());
    // true = playing the server's events on the canvas; false = the browser recording.
    const [canvasReplay, setCanvasReplay] = useState(true);
    const [desktopLoading, setDesktopLoading] = useState(true);
    const [desktopError, setDesktopError] = useState<string | null>(null);

    // Parsed desktop events for canvas replay
    const [desktopFrames, setDesktopFrames] = useState<{ instructions: { opcode: string; args: string[] }[]; offset_ms: number }[]>([]);
    const [desktopFrameIndex, setDesktopFrameIndex] = useState(0);

    // Data
    const { data: sessionData } = useQuery(['session', id], () => sessionsApi.get(id!), { enabled: !!id });
    const { data: recordingData, isLoading, error, refetch: refetchRecording, isFetching: recordingFetching } = useQuery<RecordingData>(
        ['recording', id],
        async () => { const r = await sessionsApi.getRecording(id!); return r.data; },
        { enabled: !!id }
    );

    const session = sessionData?.data;
    const sessionTypeKnown = !!recordingData;
    const sessionType = (recordingData as any)?.session_type || session?.session_type || sessionMeta?.protocol || 'terminal';
    const isDesktopSession = sessionTypeKnown && (sessionType === 'desktop' || sessionType === 'rdp');
    const isTerminalSession = sessionTypeKnown && !isDesktopSession;
    const resourceName = session?.resource_name || sessionMeta?.resource_name || '';
    const userName = session?.user_id || session?.user_email || sessionMeta?.user_id || '';

    // ==== Canvas replay instruction handler ====
    const getLayer = useCallback((index: number): LayerState => {
        if (!layersRef.current.has(index)) {
            const canvas = document.createElement('canvas');
            canvas.width = 1920; canvas.height = 1080;
            layersRef.current.set(index, { canvas, x: 0, y: 0, parent: 0 });
        }
        return layersRef.current.get(index)!;
    }, []);

    const getLayerCanvas = useCallback((index: number): HTMLCanvasElement => {
        if (index === 0 && canvasRef.current) return canvasRef.current;
        return getLayer(index).canvas;
    }, [getLayer]);

    const getLayerCtx = useCallback((index: number): CanvasRenderingContext2D | null => {
        return getLayerCanvas(index).getContext('2d');
    }, [getLayerCanvas]);

    const handleGuacInstruction = useCallback((opcode: string, args: string[]) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        switch (opcode) {
            case 'size': {
                const layerIdx = parseInt(args[0]), w = parseInt(args[1]), h = parseInt(args[2]);
                if (layerIdx === 0) {
                    const tmp = document.createElement('canvas');
                    tmp.width = canvas.width; tmp.height = canvas.height;
                    tmp.getContext('2d')?.drawImage(canvas, 0, 0);
                    canvas.width = w; canvas.height = h;
                    ctx.drawImage(tmp, 0, 0);
                } else {
                    const layer = getLayer(layerIdx);
                    const tmp = document.createElement('canvas');
                    tmp.width = layer.canvas.width; tmp.height = layer.canvas.height;
                    tmp.getContext('2d')?.drawImage(layer.canvas, 0, 0);
                    layer.canvas.width = w; layer.canvas.height = h;
                    layer.canvas.getContext('2d')?.drawImage(tmp, 0, 0);
                }
                break;
            }
            case 'img': {
                const streamIdx = args[0], mode = parseInt(args[1]), layer = parseInt(args[2]);
                const mimetype = args[3], x = parseInt(args[4]), y = parseInt(args[5]);
                streamsRef.current.set(streamIdx, { type: 'image', layer, mode, mimetype, x, y, data: '' });
                break;
            }
            case 'blob': {
                const streamIdx = args[0], b64data = args[1];
                const stream = streamsRef.current.get(streamIdx);
                if (stream && b64data) stream.data += b64data;
                break;
            }
            case 'end': {
                const streamIdx = args[0];
                const stream = streamsRef.current.get(streamIdx);
                if (!stream) break;
                if (stream.type === 'image' && stream.data) {
                    const img = new Image();
                    const { layer: layerIdx, mode, x: sx, y: sy, mimetype } = stream;
                    img.onload = () => {
                        const targetCtx = getLayerCtx(layerIdx!);
                        if (targetCtx) {
                            const prev = targetCtx.globalCompositeOperation;
                            targetCtx.globalCompositeOperation = getCompositeOp(mode!);
                            targetCtx.drawImage(img, sx!, sy!);
                            targetCtx.globalCompositeOperation = prev;
                        }
                    };
                    img.src = `data:${mimetype};base64,${stream.data}`;
                }
                streamsRef.current.delete(streamIdx);
                break;
            }
            case 'png': {
                const mode = parseInt(args[0]), layerIdx = parseInt(args[1]);
                const x = parseInt(args[2]), y = parseInt(args[3]), b64 = args[4];
                const img = new Image();
                img.onload = () => {
                    const targetCtx = getLayerCtx(layerIdx);
                    if (targetCtx) {
                        const prev = targetCtx.globalCompositeOperation;
                        targetCtx.globalCompositeOperation = getCompositeOp(mode);
                        targetCtx.drawImage(img, x, y);
                        targetCtx.globalCompositeOperation = prev;
                    }
                };
                img.src = `data:image/png;base64,${b64}`;
                break;
            }
            case 'copy': {
                const srcLI = parseInt(args[0]), srcX = parseInt(args[1]), srcY = parseInt(args[2]);
                const srcW = parseInt(args[3]), srcH = parseInt(args[4]), mode = parseInt(args[5]);
                const dstLI = parseInt(args[6]), dstX = parseInt(args[7]), dstY = parseInt(args[8]);
                const srcCV = getLayerCanvas(srcLI), dstCtx = getLayerCtx(dstLI);
                if (dstCtx && srcCV.width > 0 && srcCV.height > 0) {
                    const prev = dstCtx.globalCompositeOperation;
                    dstCtx.globalCompositeOperation = getCompositeOp(mode);
                    try {
                        if (srcLI === dstLI) {
                            const tmp = document.createElement('canvas');
                            tmp.width = srcW; tmp.height = srcH;
                            tmp.getContext('2d')?.drawImage(srcCV, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);
                            dstCtx.drawImage(tmp, dstX, dstY);
                        } else {
                            dstCtx.drawImage(srcCV, srcX, srcY, srcW, srcH, dstX, dstY, srcW, srcH);
                        }
                    } catch { }
                    dstCtx.globalCompositeOperation = prev;
                }
                break;
            }
            case 'rect': {
                const li = parseInt(args[0]), x = parseInt(args[1]), y = parseInt(args[2]);
                const w = parseInt(args[3]), h = parseInt(args[4]);
                getLayerCtx(li)?.rect(x, y, w, h);
                break;
            }
            case 'cfill': {
                const mode = parseInt(args[0]), li = parseInt(args[1]);
                const r = parseInt(args[2]), g = parseInt(args[3]), b = parseInt(args[4]), a = parseInt(args[5]);
                const tc = getLayerCtx(li);
                if (tc) {
                    const prev = tc.globalCompositeOperation;
                    tc.globalCompositeOperation = getCompositeOp(mode);
                    tc.fillStyle = guacColor(r, g, b, a);
                    tc.fill(); tc.globalCompositeOperation = prev; tc.beginPath();
                }
                break;
            }
            case 'move': {
                const li = parseInt(args[0]), pi = parseInt(args[1]);
                const x = parseInt(args[2]), y = parseInt(args[3]);
                if (li !== 0) { const l = getLayer(li); l.x = x; l.y = y; l.parent = pi; }
                break;
            }
            case 'dispose': { layersRef.current.delete(parseInt(args[0])); break; }
            case 'close': { getLayerCtx(parseInt(args[0]))?.closePath(); break; }
            case 'start': {
                const tc = getLayerCtx(parseInt(args[0]));
                if (tc) { tc.beginPath(); tc.moveTo(parseInt(args[1]), parseInt(args[2])); }
                break;
            }
            case 'sync': case 'nop': case 'ack': case 'cursor': case 'mouse':
            case 'disconnect': case 'ready': case 'error': case 'name':
            case 'set': case 'shade': case 'reset': case 'push': case 'pop':
            case 'clipboard': case 'pipe': case 'nest': case 'msg': case 'required':
                break;
        }
    }, [getLayer, getLayerCanvas, getLayerCtx]);

    // ==== TERMINAL PLAYER ====

    useEffect(() => {
        if (!isTerminalSession || !recordingData?.events) return;
        const outputs: { data: string; offset_ms: number }[] = [];
        for (const evt of recordingData.events) {
            if (evt.type !== 'output' && evt.type !== 'data') continue;
            let textData = '';
            if (evt.data) {
                // evt.data can be:
                // 1. A base64 string (Go json.Marshal encodes []byte as base64)
                // 2. An object with { data: "base64..." } from nested JSON
                // 3. A JSON string that needs parsing first
                if (typeof evt.data === 'string') {
                    // Try parsing as JSON first (nested object case)
                    try {
                        const parsed = JSON.parse(evt.data);
                        if (parsed.data) {
                            try { textData = atob(parsed.data); } catch { textData = parsed.data; }
                        } else if (parsed.output) {
                            textData = parsed.output;
                        }
                    } catch {
                        // Not JSON — likely raw base64 from Go's []byte marshal
                        try { textData = atob(evt.data); } catch { textData = evt.data; }
                    }
                } else if (typeof evt.data === 'object' && evt.data !== null) {
                    // Already parsed object
                    const inner = evt.data.data || evt.data.output || '';
                    if (inner) {
                        try { textData = atob(inner); } catch { textData = inner; }
                    }
                }
            }
            if (textData) outputs.push({ data: textData, offset_ms: evt.offset_ms });
        }
        setOutputEvents(outputs);
        const startEvt = recordingData.events.find(e => e.type === 'session_start');
        if (startEvt?.data) { try { const m = typeof startEvt.data === 'string' ? JSON.parse(startEvt.data) : startEvt.data; setSessionMeta(m.metadata || m); } catch { } }
    }, [recordingData, isTerminalSession]);

    const writeEventToTerminal = useCallback((index: number) => {
        if (!terminalRef.current || index >= outputEvents.length) return;
        terminalRef.current.write(outputEvents[index].data);
    }, [outputEvents]);

    useEffect(() => {
        if (!isTerminalSession || !isPlaying || outputEvents.length === 0) return;
        if (currentIndex >= outputEvents.length) { setIsPlaying(false); return; }
        writeEventToTerminal(currentIndex);
        const nextIndex = currentIndex + 1;
        if (nextIndex < outputEvents.length) {
            const rawDelay = (outputEvents[nextIndex].offset_ms - outputEvents[currentIndex].offset_ms) / speed;
            // Cap delay to 2s (at 1x) to prevent long idle gaps from freezing playback
            const delay = Math.min(Math.max(rawDelay, 5), 2000 / speed);
            playTimerRef.current = setTimeout(() => setCurrentIndex(nextIndex), delay);
        } else { setCurrentIndex(nextIndex); setIsPlaying(false); }
        return () => { if (playTimerRef.current) clearTimeout(playTimerRef.current); };
    }, [isTerminalSession, isPlaying, currentIndex, outputEvents, speed, writeEventToTerminal]);

    // ==== DESKTOP PLAYER (server record first, browser recording secondary) ====
    //
    // D2 = A: the server's recorded events (the JSONL record the gateway
    // writes) are the evidence, so they play first. The WebM the recorded
    // user's own browser uploaded is offered beside it, labelled as such, and
    // plays only when it is chosen or when the server record has no frames.

    useEffect(() => {
        if (!isDesktopSession || !id || !recordingData?.events) return;
        let cancelled = false;
        let objectUrl: string | null = null;

        const initDesktop = async () => {
            setDesktopLoading(true);
            setDesktopError(null);

            // 1. The server record: canvas replay from the JSONL events.
            const frames: { instructions: { opcode: string; args: string[] }[]; offset_ms: number }[] = [];
            for (const evt of recordingData.events) {
                if (evt.type !== 'output') continue;

                let rawData = '';
                if (evt.data) {
                    // evt.data could be:
                    // 1. Object: {timestamp, type, data: "base64..."} (from json.RawMessage re-serialization)
                    // 2. String: "base64string" (raw base64)
                    // 3. String: '{"type":"output","data":"base64..."}' (JSON string that needs parsing)

                    let b64 = '';
                    if (typeof evt.data === 'object' && evt.data !== null) {
                        b64 = evt.data.data || '';
                    } else if (typeof evt.data === 'string') {
                        try {
                            const parsed = JSON.parse(evt.data);
                            b64 = parsed.data || '';
                        } catch {
                            b64 = evt.data;
                        }
                    }

                    if (b64) {
                        try {
                            rawData = atob(b64);
                        } catch {
                            // Try URL-safe base64
                            try { rawData = atob(b64.replace(/-/g, '+').replace(/_/g, '/')); } catch { }
                        }
                    }
                }

                if (rawData) {
                    const instructions = parseGuacInstructions(rawData);
                    if (instructions.length > 0) {
                        frames.push({ instructions, offset_ms: evt.offset_ms });
                    }
                }
            }
            if (cancelled) return;
            setDesktopFrames(frames);
            setDesktopFrameIndex(0);

            // 2. The browser recording, if the session's browser uploaded one.
            let hasVideo = false;
            try {
                const resp = await sessionsApi.getRecordingVideo(id);
                if (!cancelled && resp.data && resp.data.byteLength > 0) {
                    objectUrl = URL.createObjectURL(new Blob([resp.data], { type: 'video/webm' }));
                    setVideoBlobUrl(objectUrl);
                    hasVideo = true;
                }
            } catch {
                // No browser recording: the server record is all there is.
            }
            if (cancelled) return;

            // The server record plays unless it has nothing to draw.
            setCanvasReplay(frames.length > 0 || !hasVideo);
            setDesktopLoading(false);
        };

        initDesktop();
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [isDesktopSession, id, recordingData]);

    // Switch between the server record and the browser recording; each
    // starts from the beginning.
    const chooseDesktopSource = (server: boolean) => {
        if (server === canvasReplay) return;
        setIsPlaying(false);
        videoRef.current?.pause();
        layersRef.current.clear(); streamsRef.current.clear();
        setDesktopFrameIndex(0);
        setCanvasReplay(server);
    };

    // Canvas replay playback
    useEffect(() => {
        if (!isDesktopSession || !canvasReplay || !isPlaying || desktopFrames.length === 0) return;
        if (desktopFrameIndex >= desktopFrames.length) { setIsPlaying(false); return; }

        // Render current frame
        const frame = desktopFrames[desktopFrameIndex];
        for (const { opcode, args } of frame.instructions) {
            handleGuacInstruction(opcode, args);
        }

        // Schedule next frame
        const nextIdx = desktopFrameIndex + 1;
        if (nextIdx < desktopFrames.length) {
            const rawDelay = (desktopFrames[nextIdx].offset_ms - frame.offset_ms) / speed;
            // Cap delay to 2s (at 1x) to prevent long idle gaps from freezing playback
            const delay = Math.min(Math.max(rawDelay, 1), 2000 / speed);
            playTimerRef.current = setTimeout(() => setDesktopFrameIndex(nextIdx), delay);
        } else {
            setDesktopFrameIndex(nextIdx);
            setIsPlaying(false);
        }

        return () => { if (playTimerRef.current) clearTimeout(playTimerRef.current); };
    }, [isDesktopSession, canvasReplay, isPlaying, desktopFrameIndex, desktopFrames, speed, handleGuacInstruction]);

    // ==== CONTROLS ====

    const handlePlay = () => {
        if (isDesktopSession) {
            if (canvasReplay) {
                if (desktopFrameIndex >= desktopFrames.length) { handleRestart(); setTimeout(() => setIsPlaying(true), 50); }
                else setIsPlaying(!isPlaying);
            } else {
                const v = videoRef.current;
                if (!v) return;
                if (v.paused) { v.play(); setIsPlaying(true); } else { v.pause(); setIsPlaying(false); }
            }
        } else {
            if (currentIndex >= outputEvents.length) { handleRestart(); setTimeout(() => setIsPlaying(true), 50); }
            else setIsPlaying(!isPlaying);
        }
    };

    const handleRestart = () => {
        if (isDesktopSession) {
            if (canvasReplay) {
                setIsPlaying(false); setDesktopFrameIndex(0);
                layersRef.current.clear(); streamsRef.current.clear();
                if (canvasRef.current) {
                    const ctx = canvasRef.current.getContext('2d');
                    if (ctx) ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                }
            } else {
                const v = videoRef.current;
                if (v) { v.currentTime = 0; v.pause(); setIsPlaying(false); }
            }
        } else {
            setIsPlaying(false); setCurrentIndex(0); terminalRef.current?.reset();
        }
    };

    const handleSpeedChange = () => {
        const s = [1, 2, 4, 8, 16];
        const next = s[(s.indexOf(speed) + 1) % s.length];
        setSpeed(next);
        if (isDesktopSession && !canvasReplay && videoRef.current) videoRef.current.playbackRate = next;
    };

    const handleSeek = (val: number) => {
        if (isDesktopSession) {
            if (canvasReplay) {
                setIsPlaying(false);
                // Reset canvas and replay up to target frame
                layersRef.current.clear(); streamsRef.current.clear();
                if (canvasRef.current) {
                    const ctx = canvasRef.current.getContext('2d');
                    if (ctx) ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                }
                const targetFrame = Math.min(val, desktopFrames.length);
                for (let i = 0; i < targetFrame; i++) {
                    for (const { opcode, args } of desktopFrames[i].instructions) {
                        handleGuacInstruction(opcode, args);
                    }
                }
                setDesktopFrameIndex(targetFrame);
            } else {
                const v = videoRef.current;
                if (v && v.duration) v.currentTime = (val / 1000) * v.duration;
            }
        } else {
            setIsPlaying(false);
            if (terminalRef.current) { terminalRef.current.reset(); for (let i = 0; i < val && i < outputEvents.length; i++) terminalRef.current.write(outputEvents[i].data); }
            setCurrentIndex(val);
        }
    };

    const toggleFullscreen = () => setIsFullscreen(!isFullscreen);

    // Video timeupdate listener
    useEffect(() => {
        if (!isDesktopSession || canvasReplay || !videoRef.current) return;
        const video = videoRef.current;
        const onTimeUpdate = () => setVideoReady(true);
        const onEnded = () => setIsPlaying(false);
        const onCanPlay = () => setVideoReady(true);
        video.addEventListener('timeupdate', onTimeUpdate);
        video.addEventListener('ended', onEnded);
        video.addEventListener('canplay', onCanPlay);
        return () => { video.removeEventListener('timeupdate', onTimeUpdate); video.removeEventListener('ended', onEnded); video.removeEventListener('canplay', onCanPlay); };
    }, [isDesktopSession, canvasReplay, videoBlobUrl]);

    // Progress calculations
    let elapsedMs: number, totalMs: number, eventCount: number, seekMax: number, seekVal: number;
    if (isDesktopSession) {
        if (canvasReplay) {
            const total = desktopFrames.length;
            elapsedMs = desktopFrameIndex > 0 && desktopFrameIndex <= total ? desktopFrames[Math.min(desktopFrameIndex, total) - 1].offset_ms : 0;
            totalMs = recordingData?.duration_ms || (total > 0 ? desktopFrames[total - 1].offset_ms : 0);
            eventCount = total;
            seekMax = total; seekVal = desktopFrameIndex;
        } else {
            const v = videoRef.current;
            elapsedMs = (v?.currentTime || 0) * 1000;
            totalMs = (v?.duration || 0) * 1000;
            eventCount = recordingData?.event_count || 0;
            seekMax = 1000; seekVal = v?.duration ? Math.floor((v.currentTime / v.duration) * 1000) : 0;
        }
    } else {
        elapsedMs = currentIndex > 0 && currentIndex <= outputEvents.length ? outputEvents[Math.min(currentIndex, outputEvents.length) - 1].offset_ms : 0;
        totalMs = recordingData?.duration_ms || 0;
        eventCount = outputEvents.length;
        seekMax = outputEvents.length; seekVal = currentIndex;
    }

    const fmtTime = (ms: number) => {
        if (!ms || isNaN(ms) || !isFinite(ms)) return '0:00';
        const s = Math.floor(ms / 1000), m = Math.floor(s / 60), h = Math.floor(m / 60);
        return h > 0 ? `${h}:${String(m % 60).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}` : `${m}:${String(s % 60).padStart(2, '0')}`;
    };

    if (isLoading) return (<LoadingRegion lines={6} lineHeight={20} label="Loading session recording" />);
    // A failed load is not "not found": only a 404 (or an empty body) means there
    // is no recording; anything else is an error the operator can retry.
    if (error && !isNotFoundError(error)) return (
        <EmptyState
            icon={<TriangleAlert aria-hidden="true" />}
            title="Failed to load recording"
            description={errorMessage(error, 'Something went wrong while loading this session recording.')}
            action={
                <div className="flex gap-2">
                    <Button variant="secondary" onClick={() => refetchRecording()} loading={recordingFetching} leadingIcon={<RotateCw className="h-4 w-4" />}>Retry</Button>
                    <Button variant="ghost" onClick={backToSessions}>Back to Sessions</Button>
                </div>
            }
        />
    );
    if (error || !recordingData) return (
        <EmptyState
            icon={<Video aria-hidden="true" />}
            title="Recording not found"
            action={<Button onClick={backToSessions}>Back to Sessions</Button>}
        />
    );

    const displayHeight = isFullscreen ? 'flex-1 min-h-0' : 'h-[60vh] min-h-[450px]';

    // Which desktop recording is on screen. The server record is the evidence
    // (D2 = A); the browser recording is said to be what it is.
    const BROWSER_LABEL = "Browser recording (uploaded by the session's own browser)";
    const sourceControl = desktopLoading ? null : videoBlobUrl && desktopFrames.length > 0 ? (
        <span className="inline-flex items-center gap-1" role="group" aria-label="Recording source">
            <Button size="sm" variant={canvasReplay ? 'secondary' : 'ghost'} aria-pressed={canvasReplay} onClick={() => chooseDesktopSource(true)}
                leadingIcon={<Palette className="h-3 w-3" aria-hidden="true" />}>
                Server record
            </Button>
            <Button size="sm" variant={canvasReplay ? 'ghost' : 'secondary'} aria-pressed={!canvasReplay} onClick={() => chooseDesktopSource(false)}
                leadingIcon={<Video className="h-3 w-3" aria-hidden="true" />}>
                {BROWSER_LABEL}
            </Button>
        </span>
    ) : (
        <Chip size="sm" variant="neutral">
            {canvasReplay
                ? <><Palette className="mr-1 inline h-3 w-3" aria-hidden="true" />Server record</>
                : <><Video className="mr-1 inline h-3 w-3" aria-hidden="true" />{BROWSER_LABEL}: the server record has no frames</>}
        </Chip>
    );

    return (
        <div className={isFullscreen ? 'zta-term-chrome fixed inset-0 z-50 flex flex-col bg-[var(--d2d-bg-primary)]' : 'flex flex-col gap-4'}>
            {/* Header. In the app shell it is the page's PageHeader (cascade plan 5.3);
                in fullscreen the player is a full-screen surface and keeps a compact bar. */}
            {isFullscreen ? (
                <div className="flex items-center justify-between px-4 pt-3 pb-2">
                    <div className="flex items-center gap-3">
                        <IconButton aria-label="Exit fullscreen" icon={<ArrowLeft />} onClick={toggleFullscreen} />
                        <div>
                            <h1 className="text-lg font-medium text-[var(--d2d-text-primary)]">
                                Session Replay {resourceName && <span className="ml-2 text-sm font-normal text-[var(--d2d-text-muted)]">— {resourceName}</span>}
                            </h1>
                            <div className="flex flex-wrap items-center gap-3 text-xs text-[var(--d2d-text-muted)]">
                            {userName && <span className="inline-flex items-center gap-1"><User className="h-3 w-3" aria-hidden="true" />{userName}</span>}
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--ds-color-bg-subtle)] text-label uppercase font-semibold">
                                {sessionType === 'desktop' ? <Monitor className="h-3 w-3" aria-hidden="true" /> : <SquareTerminal className="h-3 w-3" aria-hidden="true" />} {sessionType}
                            </span>
                            {recordingData.start_time && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{format(new Date(recordingData.start_time), 'MMM d, yyyy HH:mm')}</span>}
                            {isDesktopSession && sourceControl}
                        </div>
                        </div>
                    </div>
                    <IconButton aria-label="Exit fullscreen" size="sm" icon={<Minimize2 />} onClick={toggleFullscreen} />
                </div>
            ) : (
                <PageHeader
                    breadcrumbs={
                        <Breadcrumbs
                            items={[{ label: 'Audit' }, { label: 'Sessions', href: sessionsReturnPath() }, { label: 'Session replay' }]}
                            renderLink={(item) => (
                                <Link
                                    to={item.href!}
                                    className="rc-breadcrumbs__link"
                                    onClick={(e) => {
                                        if (!fromSessions || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                                        e.preventDefault();
                                        navigate(-1);
                                    }}
                                >
                                    {item.label}
                                </Link>
                            )}
                        />
                    }
                    title={<>Session Replay {resourceName && <span className="ml-2 text-body font-normal text-[var(--d2d-text-muted)]">— {resourceName}</span>}</>}
                    description={
                        <div className="flex flex-wrap items-center gap-3 text-xs text-[var(--d2d-text-muted)]">
                            {userName && <span className="inline-flex items-center gap-1"><User className="h-3 w-3" aria-hidden="true" />{userName}</span>}
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--ds-color-bg-subtle)] text-label uppercase font-semibold">
                                {sessionType === 'desktop' ? <Monitor className="h-3 w-3" aria-hidden="true" /> : <SquareTerminal className="h-3 w-3" aria-hidden="true" />} {sessionType}
                            </span>
                            {recordingData.start_time && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{format(new Date(recordingData.start_time), 'MMM d, yyyy HH:mm')}</span>}
                            {isDesktopSession && sourceControl}
                        </div>
                    }
                    secondaryActions={<IconButton aria-label="Fullscreen" tooltip="Fullscreen" size="sm" icon={<Maximize2 />} onClick={toggleFullscreen} />}
                />
            )}

            {/* Player: canvas + controls, dark in both app themes */}
            <div className={isFullscreen ? 'flex min-h-0 flex-1 flex-col' : 'zta-term-chrome flex flex-col overflow-hidden rounded-lg border border-[var(--d2d-border)] bg-[var(--d2d-bg-primary)]'}>
                {/* Display */}
                {!sessionTypeKnown ? (
                    <div
                        className={`flex items-center justify-center overflow-hidden ${displayHeight} ${isFullscreen ? 'mx-4 rounded-lg border border-[var(--d2d-border)]' : ''}`}
                        style={{ backgroundColor: canvasBackground.replay }}
                    >
                        <div className="text-center"><Spinner size={40} className="mx-auto mb-3 block w-fit text-[var(--d2d-text-primary)]" /><p className="text-[var(--d2d-text-muted)] text-sm">Detecting session type...</p></div>
                    </div>
                ) : isDesktopSession ? (
                    <div
                        className={`relative overflow-hidden ${displayHeight} ${isFullscreen ? 'mx-4 rounded-lg border border-[var(--d2d-border)]' : ''}`}
                        style={{ backgroundColor: canvasBackground.desktop }}
                    >
                        {/* Browser recording (secondary: uploaded by the session's own browser) */}
                        {videoBlobUrl && !canvasReplay && (
                            <video ref={videoRef} src={videoBlobUrl}
                                className="h-full w-full object-contain"
                                style={{ backgroundColor: canvasBackground.desktop }}
                                playsInline preload="auto"
                            />
                        )}
                        {/* Server record: canvas replay of the JSONL events (the evidence) */}
                        {canvasReplay && (
                            <div className="flex h-full w-full items-center justify-center overflow-hidden">
                                <canvas ref={canvasRef} width={1920} height={1080}
                                    className="max-h-full max-w-full object-contain"
                                />
                            </div>
                        )}
                        {/* Loading overlay */}
                        {desktopLoading && (
                            <div className="absolute inset-0 flex items-center justify-center z-10" style={{ backgroundColor: canvasBackground.replay }}>
                                <div className="text-center">
                                    <Spinner size={40} className="mx-auto mb-3 block w-fit text-[var(--d2d-text-primary)]" />
                                    <p className="text-[var(--d2d-text-muted)] text-sm">Loading desktop recording...</p>
                                </div>
                            </div>
                        )}
                        {desktopError && (
                            <div className="absolute inset-0 flex items-center justify-center z-10" style={{ backgroundColor: canvasBackground.replay }}>
                                <div className="text-center p-8">
                                    <TriangleAlert className="mx-auto mb-3 h-12 w-12 text-[var(--danger-text)]" aria-hidden="true" />
                                    <p className="text-[var(--danger-text)] font-medium mb-2">Playback Error</p>
                                    <p className="text-[var(--d2d-text-muted)] text-sm max-w-md">{desktopError}</p>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <TerminalSurface
                        theme={replayTerminalTheme}
                        readOnly
                        aria-label="Session replay"
                        className={`${displayHeight} ${isFullscreen ? 'mx-4 rounded-lg border border-[var(--d2d-border)]' : ''}`}
                        onReady={(term) => { terminalRef.current = term; }}
                        onDispose={() => { terminalRef.current = null; }}
                    />
                )}

                {/* Controls */}
                <div className={isFullscreen ? 'px-4 pt-2 pb-3' : 'border-t border-[var(--d2d-border)] bg-[var(--d2d-bg-secondary)] p-3'}>
                    <Slider
                        aria-label="Seek"
                        className="mb-3"
                        min={0}
                        max={Math.max(seekMax, 1)}
                        value={seekVal}
                        onChange={handleSeek}
                        disabled={seekMax === 0}
                        formatValue={() => `${fmtTime(elapsedMs)} / ${fmtTime(totalMs)}`}
                    />
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-mono min-w-[100px] text-[var(--d2d-text-muted)]">{fmtTime(elapsedMs)} / {fmtTime(totalMs)}</span>
                        <div className="flex items-center gap-2">
                            <IconButton aria-label="Restart" size="sm" icon={<Rewind />} onClick={handleRestart} />
                            <IconButton
                                aria-label={isPlaying ? 'Pause' : 'Play'}
                                size="lg"
                                icon={isPlaying ? <Pause /> : <Play className="ml-0.5" />}
                                onClick={handlePlay}
                                className="rounded-full bg-[var(--action-primary)] text-[var(--action-primary-text)] hover:bg-[var(--action-primary-hover)]"
                            />
                            <Button variant="secondary" size="sm" onClick={handleSpeedChange} className="min-w-[3rem] font-mono">{speed}x</Button>
                        </div>
                        <span className="text-xs min-w-[100px] text-right text-[var(--d2d-text-muted)]">{eventCount} events</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
